import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import { Box, DialogActions, Grid } from "@mui/material";
import Typography from "@mui/material/Typography/Typography";
import { AlertContext } from "contexts/Alert";
import { OnboardingDialogsContext, Page } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import {
  Dispatch,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { routes } from "schemas/routes";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import TemplateBody from "features/Influencer/Templates/SharedComponents/TemplateBody";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import { getHighlightedContent } from "utils/string";
import { trackEvent } from "utils/tracking";
import { useGamification } from "utils/useGamification";

import TemplateRubrics from "../../TemplateRubrics";
import OneTweak from "./OneTweak";

interface Props {
  editTemplate: Template;
  setEditTemplate: Dispatch<React.SetStateAction<Template>>;
  followUpTemplate?: Template;
  setFollowUpTemplate?: Dispatch<React.SetStateAction<Template>>;
  handleNext: () => void;
  allowChangeTitle?: boolean;
  setIsDirty: Dispatch<React.SetStateAction<boolean>>;
}
export interface ReviewBentoAiTemplateHandle {
  save: () => Promise<void>;
}

const ReviewBentoAiTemplate = forwardRef<ReviewBentoAiTemplateHandle, Props>(
  (
    {
      editTemplate,
      setEditTemplate,
      handleNext,
      followUpTemplate,
      setFollowUpTemplate,
      setIsDirty,
      allowChangeTitle = false,
    },
    ref,
  ) => {
    const { setErrorAlert } = useContext(AlertContext);
    const { getToken } = useAuth();
    const { currentOrg } = useContext(OrganizationUserContext);
    const { page, setPage } = useContext(OnboardingDialogsContext);
    const { addTemplateTaskCompleted } = useGamification();
    const location = useLocation();

    const [saveLoading, setSaveLoading] = useState<boolean>(false);
    const isVaOnboarding = isPage(routes.templatesSelfServeOnboarding);
    const [originalHighlightPortion, setOriginalHighlightPortion] =
      useState("");
    const [currentStep, setCurrentStep] = useState<"outreach" | "followup">(
      "outreach",
    );
    const [openDiscardWarning, setOpenDiscardWarning] =
      useState<boolean>(false);
    const [openBackWarning, setOpenBackWarning] = useState<boolean>(false);
    const [restoreSignal, setRestoreSignal] = useState<number>(0);

    const isFollowupStep = currentStep === "followup";
    const hasFollowup =
      !!followUpTemplate &&
      !!setFollowUpTemplate &&
      !!_stripHtml(followUpTemplate.body || "");
    const activeTemplate =
      isFollowupStep && followUpTemplate ? followUpTemplate : editTemplate;
    const setActiveTemplate =
      isFollowupStep && setFollowUpTemplate
        ? setFollowUpTemplate
        : setEditTemplate;

    const handleRestoreContent = (subject: string, body: string) => {
      setActiveTemplate((prev) => ({
        ...prev,
        body,
        ...(subject ? { subject } : {}),
      }));
      setRestoreSignal((s) => s + 1);
    };

    const highlightTemplate = async () => {
      // For AI template that user hasn't made edited to,
      // fetch the highlighted version.
      if (editTemplate?.contentType !== TemplateContentType.OUTREACH) {
        return;
      }
      try {
        setSaveLoading(true);
        const res = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg?.id}/templates/${editTemplate?.id}/highlight`,
          "GET",
        );
        if (res.body) {
          setOriginalHighlightPortion(getHighlightedContent(res.body));
          setEditTemplate((prev) => ({
            ...prev,
            body: res.body,
          }));
        }
      } catch (error) {
        setErrorAlert(error);
      } finally {
        setSaveLoading(false);
      }
    };

    const saveOutreachTemplate = async () => {
      const hasEditedHighlightSentences =
        !originalHighlightPortion ||
        !editTemplate.body?.includes(originalHighlightPortion);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/${editTemplate?.id}`,
        "PUT",
        {},
        {
          ...editTemplate,
          validateBulkSendVariables: Boolean(isVaOnboarding),
          hasEditedHighlightSentences,
        },
      );
      setEditTemplate(res.template);
      addTemplateTaskCompleted(TemplateContentType.OUTREACH);
    };

    const saveFollowupTemplate = async () => {
      if (!followUpTemplate?.id) return;
      const { template: followup } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/${followUpTemplate.id}`,
        "PUT",
        {},
        { ...followUpTemplate },
      );
      if (setFollowUpTemplate) setFollowUpTemplate(followup);
      addTemplateTaskCompleted(TemplateContentType.FOLLOWUP);
    };

    const handleSaveCurrent = async () => {
      setIsDirty(false);
      try {
        setSaveLoading(true);
        if (isFollowupStep) {
          await saveFollowupTemplate();
          if (handleNext) handleNext();
        } else {
          await saveOutreachTemplate();
          if (hasFollowup) {
            setCurrentStep("followup");
          } else if (handleNext) {
            handleNext();
          }
        }
      } catch (error) {
        setErrorAlert(error);
      } finally {
        setSaveLoading(false);
      }
    };

    const handleSaveBoth = async () => {
      // "Save As Is": save both templates in
      // one shot regardless of which step we're on, since the user is bypassing
      // the per-step review.
      setIsDirty(false);
      try {
        setSaveLoading(true);
        await saveOutreachTemplate();
        if (hasFollowup) {
          await saveFollowupTemplate();
        }
        if (handleNext) handleNext();
      } catch (error) {
        setErrorAlert(error);
      } finally {
        setSaveLoading(false);
      }
    };

    const onBackClick = () => {
      if (isFollowupStep) {
        setOpenDiscardWarning(true);
        return;
      }
      setOpenBackWarning(true);
    };

    const discardTemplateAndRestart = async () => {
      setOpenBackWarning(false);
      const outreachId = editTemplate?.id;
      const followupId = followUpTemplate?.id;

      setEditTemplate({
        name: "",
        contentType: TemplateContentType.OUTREACH,
        subject: "",
        body: "",
      });
      if (setFollowUpTemplate) {
        setFollowUpTemplate({
          name: "",
          contentType: TemplateContentType.FOLLOWUP,
          subject: "",
          body: "",
        });
      }
      setIsDirty(false);
      setPage(Page.pitchType);
      trackEvent("Create Template Dialog Form - Discard And Restart", {
        page,
        route: location.pathname,
      });

      if (outreachId) {
        try {
          await fetcherAuth(
            getToken,
            `/api/organization/${currentOrg?.id}/templates/${outreachId}`,
            "DELETE",
          );
        } catch (error) {
          setErrorAlert(error);
        }
      }
      if (followupId) {
        try {
          await fetcherAuth(
            getToken,
            `/api/organization/${currentOrg?.id}/templates/${followupId}`,
            "DELETE",
          );
        } catch (error) {
          setErrorAlert(error);
        }
      }
    };

    const discardFollowupAndGoBack = async () => {
      setOpenDiscardWarning(false);
      try {
        if (followUpTemplate?.id) {
          await fetcherAuth(
            getToken,
            `/api/organization/${currentOrg?.id}/templates/${followUpTemplate.id}`,
            "DELETE",
          );
        }
      } catch (error) {
        setErrorAlert(error);
      }
      if (setFollowUpTemplate) {
        setFollowUpTemplate({
          name: "",
          contentType: TemplateContentType.FOLLOWUP,
          subject: "",
          body: "",
        });
      }
      setCurrentStep("outreach");
    };

    useEffect(() => {
      if (editTemplate?.isAiGenerated) {
        highlightTemplate();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editTemplate?.id]);

    useImperativeHandle(ref, () => ({
      save: handleSaveBoth,
    }));

    if (!editTemplate) {
      return <></>;
    }
    return (
      <Box sx={isPage(routes.onboardingVaSelfServe) ? { p: 3 } : {}}>
        <Typography variant="h4" gutterBottom>
          {isFollowupStep
            ? "Review Your Follow-up Template"
            : "Review Your Outreach Template"}
        </Typography>
        <Grid container spacing={3} alignItems="flex-start">
          {/* Left column */}
          <Grid item xs={12} md={8}>
            <>
              <Box component="ul" sx={{ pl: 0 }}>
                {isVaOnboarding ? (
                  <li style={{ listStyleType: "none" }}>
                    <Typography>
                      🚨 Make sure your templates{" "}
                      <strong>do not contain</strong> any other variables beside{" "}
                      <Box component="span" sx={{ color: "purple.dark" }}>
                        {"{Brand Name}"}
                      </Box>{" "}
                      and{" "}
                      <Box component="span" sx={{ color: "purple.dark" }}>
                        {"{First Name}"}
                      </Box>
                      .
                    </Typography>
                  </li>
                ) : (
                  <></>
                )}
              </Box>
            </>
            <TemplateBody
              editTemplate={activeTemplate}
              setEditTemplate={setActiveTemplate}
              isNew={true}
              allowChangeTitle={allowChangeTitle}
              minimalist
              displayError={true}
              setIsDirty={setIsDirty}
              restoreSignal={restoreSignal}
            />

            {!isFollowupStep && originalHighlightPortion && <OneTweak />}
          </Grid>

          {/* Right column — Suggestions */}
          <Grid item xs={12} md={4} sx={{ order: { xs: -1, md: 1 } }}>
            <TemplateRubrics
              template={activeTemplate}
              setTemplate={setActiveTemplate}
              onRestoreContent={handleRestoreContent}
            />
          </Grid>
        </Grid>

        <DialogActions>
          <Grid container columnGap={1} justifyContent={"space-between"}>
            <LoadingButton
              disableElevation
              type="submit"
              onClick={onBackClick}
              loading={saveLoading}
              sx={{ px: 3, py: 1 }}
            >
              Back
            </LoadingButton>

            <LoadingButton
              disableElevation
              variant="contained"
              type="submit"
              onClick={handleSaveCurrent}
              loading={saveLoading}
              sx={{ px: 3, py: 1 }}
            >
              Next{" "}
            </LoadingButton>
          </Grid>
        </DialogActions>

        <ConfirmDialog
          header="Discard this template?"
          subheader="Going back will delete this follow-up template. You'll be returned to your outreach template."
          open={openDiscardWarning}
          handleClose={() => setOpenDiscardWarning(false)}
          handleCancel={() => setOpenDiscardWarning(false)}
          cancelText="Keep editing"
          confirmText="Discard"
          handleConfirm={discardFollowupAndGoBack}
        />

        <ConfirmDialog
          header="Discard this template?"
          subheader="Going back will delete this template and restart the template creation process."
          open={openBackWarning}
          handleClose={() => setOpenBackWarning(false)}
          handleCancel={() => setOpenBackWarning(false)}
          cancelText="Keep editing"
          confirmText="Discard"
          handleConfirm={discardTemplateAndRestart}
        />
      </Box>
    );
  },
);

ReviewBentoAiTemplate.displayName = "ReviewBentoAiTemplate";

export default ReviewBentoAiTemplate;

const _stripHtml = (html: string) => html.replace(/(<([^>]+)>)/gi, "").trim();
